import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tag, Plus, Trash2, Bell, Send, Pencil, Copy, Search, BarChart3,
  Calendar, Users, Mail, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { ActionMenu } from '../../components/crm/CrmListShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isExpired = (d) => {
  if (!d.valid_until) return false;
  return new Date(d.valid_until) < new Date(new Date().toISOString().slice(0, 10));
};
const isMaxed = (d) => {
  const max = Number(d.max_uses || 0);
  return max > 0 && Number(d.uses || 0) >= max;
};

export const CrmDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDiscount, setEditDiscount] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Usage viewer
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);

  // Send to segment
  const [sendOpen, setSendOpen] = useState(false);
  const [sendDisc, setSendDisc] = useState(null);
  const [sendForm, setSendForm] = useState({ segment: 'all', days: '60', channel: 'email' });
  const [sending, setSending] = useState(false);

  // Create form
  const [code, setCode] = useState('');
  const [type, setType] = useState('percent');
  const [value, setValue] = useState('10');
  const [desc, setDesc] = useState('');
  const [maxUses, setMaxUses] = useState('0');
  const [autoReminder, setAutoReminder] = useState(false);
  const [reminderDays, setReminderDays] = useState('30');
  const [validUntil, setValidUntil] = useState('');

  useEffect(() => { fetchDiscounts(); }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/discounts`, { withCredentials: true });
      setDiscounts(res.data);
    } catch { toast.error('Failed to load discounts'); }
    finally { setLoading(false); }
  };

  const autoCode = () => {
    const base = 'PARTY';
    const rand = Math.random().toString(36).slice(2, 7).toUpperCase();
    setCode(`${base}${rand}`);
  };

  const createDiscount = async () => {
    if (!value) { toast.error('Value is required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/discounts`, {
        code: code || undefined,
        type, value: parseFloat(value),
        description: desc,
        max_uses: parseInt(maxUses) || 0,
        auto_reminder: autoReminder,
        reminder_days_after: parseInt(reminderDays) || 30,
        valid_until: validUntil || undefined,
      }, { withCredentials: true });
      toast.success('Discount created');
      setCreateOpen(false);
      resetForm();
      fetchDiscounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateDiscount = async () => {
    if (!editDiscount) return;
    try {
      await axios.patch(`${API_URL}/api/admin/discounts/${editDiscount.discount_id}`, {
        active: editDiscount.active,
        code: editDiscount.code,
        type: editDiscount.type,
        value: editDiscount.value,
        description: editDiscount.description,
        max_uses: editDiscount.max_uses,
        auto_reminder: editDiscount.auto_reminder,
        reminder_days_after: editDiscount.reminder_days_after,
        valid_until: editDiscount.valid_until,
      }, { withCredentials: true });
      toast.success('Discount updated');
      setEditOpen(false);
      fetchDiscounts();
    } catch { toast.error('Failed'); }
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm('Delete this discount? It will be soft-deleted and hidden.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/discounts/${id}`, { withCredentials: true });
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch { toast.error('Failed'); }
  };

  const duplicateDiscount = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/discounts/${id}/duplicate`, {}, { withCredentials: true });
      toast.success(`Duplicated as ${res.data.code}`);
      fetchDiscounts();
    } catch { toast.error('Failed to duplicate'); }
  };

  const openUsage = async (d) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/discounts/${d.discount_id}/usage`, { withCredentials: true });
      setUsageData(res.data);
      setUsageOpen(true);
    } catch { toast.error('Failed to load usage'); }
  };

  const openSend = (d) => {
    setSendDisc(d);
    setSendForm({ segment: 'all', days: '60', channel: 'email' });
    setSendOpen(true);
  };

  const submitSend = async () => {
    if (!sendDisc) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/discounts/${sendDisc.discount_id}/send-to-segment`,
        { segment: sendForm.segment, days: Number(sendForm.days) || 60, channel: sendForm.channel },
        { withCredentials: true },
      );
      toast.success(`Sent ${res.data.sent} · skipped ${res.data.skipped}`);
      setSendOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const sendAutoReminders = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/discounts/send-reminders`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch { toast.error('Failed'); }
  };

  const resetForm = () => {
    setCode(''); setType('percent'); setValue('10'); setDesc(''); setMaxUses('0');
    setAutoReminder(false); setReminderDays('30'); setValidUntil('');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return discounts.filter((d) => {
      if (q && !(d.code || '').toLowerCase().includes(q) && !(d.description || '').toLowerCase().includes(q)) return false;
      if (statusFilter === 'active' && (!d.active || isExpired(d) || isMaxed(d))) return false;
      if (statusFilter === 'expired' && !isExpired(d)) return false;
      if (statusFilter === 'maxed' && !isMaxed(d)) return false;
      if (statusFilter === 'inactive' && d.active) return false;
      return true;
    });
  }, [discounts, search, statusFilter]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Discounts & Reminders</h1>
          <p className="text-[#6B6B7A]">Create discount codes, send them in bulk, and see how each one performs.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={sendAutoReminders} variant="outline" className="rounded-full border-[#E8E5DF]" data-testid="send-auto-reminders">
            <Bell className="w-4 h-4 mr-2" /> Run Auto Reminders
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="create-discount-btn">
            <Plus className="w-4 h-4 mr-2" /> New Discount
          </Button>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex gap-2 flex-wrap mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code or description…"
            className="pl-9 rounded-full border-[#E8E5DF] bg-white"
            data-testid="discount-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-full bg-white border-[#E8E5DF]" data-testid="discount-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All discounts</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="maxed">Fully used</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Discount cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
          <Tag className="w-12 h-12 text-[#6B6B7A] mx-auto mb-4" />
          <p className="text-[#6B6B7A]">
            {discounts.length === 0 ? 'No discounts yet. Create your first one!' : 'No discounts match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d, i) => {
            const expired = isExpired(d);
            const maxed = isMaxed(d);
            return (
              <motion.div key={d.discount_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className={`bg-white rounded-2xl border p-5 card-hover ${d.active && !expired && !maxed ? 'border-[#E8E5DF]' : 'border-red-200 opacity-80'}`}
                data-testid={`discount-card-${d.discount_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-mono text-lg font-bold text-[#FF3B6B]">{d.code}</p>
                    <p className="text-sm text-[#6B6B7A] line-clamp-2">{d.description || (d.type === 'percent' ? `${d.value}% off` : `$${d.value} off`)}</p>
                  </div>
                  <ActionMenu
                    testid={`discount-actions-${d.discount_id}`}
                    items={[
                      { label: 'View usage', icon: BarChart3, onClick: () => openUsage(d), testid: `usage-disc-${d.discount_id}` },
                      { label: 'Send to customer', icon: Send, onClick: () => openSend(d), testid: `send-disc-${d.discount_id}` },
                      { label: 'Edit', icon: Pencil, onClick: () => { setEditDiscount({ ...d }); setEditOpen(true); }, testid: `edit-disc-${d.discount_id}` },
                      { label: 'Duplicate', icon: Copy, onClick: () => duplicateDiscount(d.discount_id), testid: `dup-disc-${d.discount_id}` },
                      { separator: true },
                      { label: 'Delete', icon: Trash2, destructive: true, onClick: () => deleteDiscount(d.discount_id), testid: `del-disc-${d.discount_id}` },
                    ]}
                  />
                </div>

                <div className="space-y-1.5 text-sm mb-4">
                  <Row label="Value" value={d.type === 'percent' ? `${d.value}%` : `$${d.value}`} />
                  <Row label="Uses" value={`${d.uses || 0}/${d.max_uses || '∞'}`} />
                  <Row label="Valid until" value={d.valid_until || '—'} />
                  <Row label="Auto reminder" value={d.auto_reminder ? `After ${d.reminder_days_after}d` : 'Off'} />
                  <div className="flex justify-between items-center pt-2 border-t border-[#E8E5DF]">
                    <span className="text-[#6B6B7A]">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      maxed ? 'bg-gray-100 text-gray-700'
                        : expired ? 'bg-red-100 text-red-700'
                        : d.active ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {maxed ? 'Fully used' : expired ? 'Expired' : d.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openUsage(d)} className="flex-1 h-8 rounded-full text-xs" data-testid={`usage-disc-card-${d.discount_id}`}>
                    <BarChart3 className="w-3.5 h-3.5 mr-1" /> Usage
                  </Button>
                  <Button size="sm" onClick={() => openSend(d)} className="flex-1 h-8 rounded-full text-xs bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid={`send-disc-card-${d.discount_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Send
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Create Discount Code</DialogTitle>
            <DialogDescription>Set the code, type (% or $), and validity window. Leave code blank to auto-generate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Code (auto-generated if empty)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g., PARTY2026" className="rounded-xl border-[#E8E5DF] font-mono" data-testid="disc-code" />
                <Button type="button" variant="outline" onClick={autoCode} className="rounded-xl" data-testid="disc-autogen">Auto</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Value *</Label>
                <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="10" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="disc-value" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Description</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="e.g., Returning customer offer" className="mt-1 rounded-xl border-[#E8E5DF]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Max Uses (0 = unlimited)</Label>
                <Input type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="disc-max-uses" />
              </div>
              <div>
                <Label className="font-semibold">Valid Until</Label>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="disc-valid-until" />
              </div>
            </div>

            <div className="bg-[#FFFDF8] rounded-xl p-4 border border-[#E8E5DF]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-semibold text-[#1E1E24]">Auto Re-booking Reminder</p>
                  <p className="text-xs text-[#6B6B7A]">Automatically send this code to past customers</p>
                </div>
                <Switch checked={autoReminder} onCheckedChange={setAutoReminder} data-testid="auto-reminder-switch" />
              </div>
              {autoReminder && (
                <div>
                  <Label className="font-semibold text-sm">Days after last party</Label>
                  <Input type="number" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="reminder-days" />
                </div>
              )}
            </div>

            <Button onClick={createDiscount} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-create-disc">
              Create Discount
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Discount</DialogTitle>
            <DialogDescription>Update the discount details. Existing usage history stays intact.</DialogDescription>
          </DialogHeader>
          {editDiscount && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Code</Label>
                <Input value={editDiscount.code} onChange={(e) => setEditDiscount((p) => ({ ...p, code: e.target.value.toUpperCase() }))} className="mt-1 rounded-xl border-[#E8E5DF] font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Type</Label>
                  <Select value={editDiscount.type} onValueChange={(v) => setEditDiscount((p) => ({ ...p, type: v }))}>
                    <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-semibold">Value</Label>
                  <Input type="number" value={editDiscount.value} onChange={(e) => setEditDiscount((p) => ({ ...p, value: parseFloat(e.target.value) }))} className="mt-1 rounded-xl border-[#E8E5DF]" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Valid Until</Label>
                <Input type="date" value={editDiscount.valid_until || ''} onChange={(e) => setEditDiscount((p) => ({ ...p, valid_until: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" />
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]">
                <span className="font-semibold text-[#1E1E24]">Active</span>
                <Switch checked={editDiscount.active} onCheckedChange={(v) => setEditDiscount((p) => ({ ...p, active: v }))} />
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]">
                <span className="font-semibold text-[#1E1E24]">Auto Reminder</span>
                <Switch checked={editDiscount.auto_reminder} onCheckedChange={(v) => setEditDiscount((p) => ({ ...p, auto_reminder: v }))} />
              </div>
              {editDiscount.auto_reminder && (
                <div>
                  <Label className="font-semibold text-sm">Reminder days after party</Label>
                  <Input type="number" value={editDiscount.reminder_days_after} onChange={(e) => setEditDiscount((p) => ({ ...p, reminder_days_after: parseInt(e.target.value) }))} className="mt-1 rounded-xl border-[#E8E5DF]" />
                </div>
              )}
              <Button onClick={updateDiscount} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="confirm-edit-disc">
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Usage viewer */}
      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Discount Usage — {usageData?.discount?.code}</DialogTitle>
            <DialogDescription>
              {usageData
                ? `${usageData.usage_count} booking(s) used this code · total discount given $${usageData.total_discount_given.toFixed(2)}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          {usageData?.bookings?.length === 0 ? (
            <p className="text-sm text-[#6B6B7A] italic">No bookings have used this code yet.</p>
          ) : (
            <div className="space-y-2" data-testid="usage-list">
              {usageData?.bookings?.map((b) => (
                <div key={b.booking_id} className="flex items-center justify-between p-3 rounded-xl border border-[#E8E5DF] bg-[#FFFDF8]">
                  <div>
                    <p className="text-sm font-semibold text-[#1E1E24]">{b.customer_name || b.customer_email}</p>
                    <p className="text-xs text-[#6B6B7A]"><Calendar className="w-3 h-3 inline mr-1" />{b.date} · {b.package_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#1E1E24]">${Number(b.total || 0).toFixed(2)}</p>
                    <p className="text-xs text-[#06D6A0]">-${Number((b.discount || {}).amount || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send to segment */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Send <span className="font-mono text-[#FF3B6B]">{sendDisc?.code}</span> to customers</DialogTitle>
            <DialogDescription>Pick a segment — the code is embedded in every message.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-semibold">Segment</Label>
              <Select value={sendForm.segment} onValueChange={(v) => setSendForm((p) => ({ ...p, segment: v }))}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-segment"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All customers who have ever booked</SelectItem>
                  <SelectItem value="recent_booked">Customers who booked within last N days</SelectItem>
                  <SelectItem value="unbooked">Customers who haven't booked in N days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sendForm.segment !== 'all' && (
              <div>
                <Label className="font-semibold">N (days)</Label>
                <Input type="number" value={sendForm.days} onChange={(e) => setSendForm((p) => ({ ...p, days: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-days" />
              </div>
            )}
            <div>
              <Label className="font-semibold">Channel</Label>
              <Select value={sendForm.channel} onValueChange={(v) => setSendForm((p) => ({ ...p, channel: v }))}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="send-channel"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="email"><Mail className="w-3 h-3 inline mr-1" /> Email</SelectItem>
                  <SelectItem value="sms"><MessageSquare className="w-3 h-3 inline mr-1" /> SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF] text-xs text-[#6B6B7A]">
              <Users className="w-3 h-3 inline mr-1" /> Messages are logged to the Communications tab and remain MOCKED until SendGrid / Twilio keys are connected.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSendOpen(false)} className="flex-1 rounded-full" disabled={sending}>Cancel</Button>
              <Button onClick={submitSend} disabled={sending} className="flex-1 rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="confirm-send">
                <Send className="w-4 h-4 mr-1" />{sending ? 'Sending…' : 'Send Now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[#6B6B7A]">{label}</span>
    <span className="text-[#1E1E24]">{value}</span>
  </div>
);

export default CrmDiscounts;
